import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './RespondToSuggestion.css';

const API = process.env.REACT_APP_API_BASE_URL;

// v3's reveal stages, in order — used to answer "have we reached at least
// stage X yet" (V3_STAGE_ORDER.indexOf(v3Stage) >= V3_STAGE_ORDER.indexOf('x'))
// rather than exact-matching one stage, since once a later stage is
// reached everything unlocked by an earlier one must stay visible too.
const V3_STAGE_ORDER = ['delay', 'typing1', 'leadCard', 'typing2', 'restCards', 'closing'];

// v3 only — types each paragraph out character by character, pausing
// briefly between paragraphs, rather than rendering the greeting/intro as
// static text. Runs once per mount (paraIndex/charIndex only ever advance
// forward), so it doesn't retype on unrelated re-renders — RespondToSuggestion
// only mounts this once context has loaded, and its own state updates
// (button clicks etc.) don't unmount/remount it.
// Each paragraph is either a plain string or { text, emphasis, link } —
// emphasis (bool) is for the one "greeting" line that should render
// bigger/bold, explicit rather than a CSS `:first-child` selector, since v3
// renders several separate TypewriterIntro instances in sequence and a
// `:first-child` rule would wrongly re-apply the greeting style to
// whichever line happens to lead each one. link ({ text, to }), if present,
// swaps that substring for a real clickable Link once the line has
// finished typing — kept as plain text while still typing so the animation
// doesn't have to deal with an interactive element mid-character-reveal.
const normalizeParagraph = (p) => (typeof p === 'string' ? { text: p, emphasis: false, link: null } : p);

const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

// Below 768px, index.css's mobile layout rule ("Ensure html, body, and
// #root take full height on mobile") gives both `html` and `body` an
// explicit height — which, as a side effect, makes `body` its own
// independently scrollable box instead of the normal window-level scroll.
// window.scrollY/scrollTo are then completely disconnected from what's
// actually on screen (always reads/sets 0, since documentElement itself
// never overflows). Which of the two actually ends up as the real
// scrolling container isn't consistent across engines — desktop Chrome's
// device emulation (verified) picks one, real iOS Safari (reported, can't
// verify directly) apparently picks the other — so rather than guess via a
// scrollHeight/clientHeight heuristic, every read/write below just targets
// BOTH unconditionally. Only the one that's actually overflowing responds;
// the other is a harmless no-op (it clamps right back to 0), so this can't
// cause double-scrolling.
const getScrollTop = () => Math.max(document.documentElement.scrollTop, document.body.scrollTop);
const setScrollTop = (top, behavior) => {
  document.documentElement.scrollTo({ top, left: 0, behavior });
  document.body.scrollTo({ top, left: 0, behavior });
};

// The browser's own `scrollTo({behavior: 'smooth'})` runs at a fixed,
// fairly quick duration with no way to slow it down — this animates the
// scroll manually instead, over a configurable duration with easing, so it
// reads as a slow, deliberate glide rather than a quick snap.
const animateScrollTo = (targetY, duration = 1100) => {
  const startY = getScrollTop();
  const delta = targetY - startY;
  if (Math.abs(delta) < 1) return;
  const startTime = performance.now();
  const step = (now) => {
    const progress = Math.min(1, (now - startTime) / duration);
    // behavior: 'auto' (instant) is required here — the site-wide CSS sets
    // `html { scroll-behavior: smooth }`, and without overriding it, each of
    // these ~60-times-a-second calls would kick off the browser's own
    // smooth-scroll animation toward a slightly-different target, fighting
    // this manual easing loop and leaving the page barely moving at all.
    setScrollTop(startY + delta * easeInOutQuad(progress), 'auto');
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

// v3 only — as content grows downward (new paragraph, new card block), keep
// the newest content settling around the vertical middle of the screen
// (like a teleprompter) rather than jammed against the bottom edge, so
// there's always visible context above it. `endRef` must point at a
// sentinel element sitting at the very bottom of the page's OWN content
// (not document.body — that also includes the site navbar above this page,
// which alone can already exceed half a screen's height, causing this to
// fire almost immediately regardless of how far into the reveal we
// actually are). Does nothing while content's bottom edge is still above
// the midpoint — only once it would grow past it does this pull it back
// down to sit exactly on that line.
const MIDPOINT_FRACTION = 0.5;
const scrollToBottomSmoothly = (endRef) => {
  requestAnimationFrame(() => {
    if (!endRef?.current) return;
    const rect = endRef.current.getBoundingClientRect();
    const midpoint = window.innerHeight * MIDPOINT_FRACTION;
    if (rect.bottom <= midpoint) return;
    animateScrollTo(getScrollTop() + (rect.bottom - midpoint));
  });
};

// Deliberately doesn't scroll on its own — the two real scroll moments (a
// person card appearing, and the beat just before the next text block
// starts) are both driven by RespondToSuggestion's own stage effects
// instead, so typing mid-paragraph never nudges the page underneath it.
// instant: skips the character-by-character animation entirely and renders
// fully typed on mount (onComplete still fires, just immediately) — used
// when the recipient has already sat through this reveal once this session
// (see RespondToSuggestion's skipAnimations), so navigating back to
// /respond a second time doesn't replay it.
const TypewriterIntro = ({ paragraphs, speed = 28, onComplete, instant = false, startDelay = 0 }) => {
  const normalized = paragraphs.map(normalizeParagraph);
  const [paraIndex, setParaIndex] = useState(instant ? Math.max(0, normalized.length - 1) : 0);
  const [charIndex, setCharIndex] = useState(instant ? (normalized[normalized.length - 1]?.text.length || 0) : 0);
  const [done, setDone] = useState(instant || normalized.length === 0);
  const [started, setStarted] = useState(instant || startDelay <= 0);

  useEffect(() => {
    if (instant || started || startDelay <= 0) return undefined;
    const t = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(t);
  }, [instant, started, startDelay]);

  useEffect(() => {
    if (instant || done || !started) return undefined;
    const current = normalized[paraIndex]?.text || '';
    if (charIndex >= current.length) {
      if (paraIndex >= normalized.length - 1) {
        setDone(true);
        return undefined;
      }
      const pause = setTimeout(() => {
        setParaIndex((i) => i + 1);
        setCharIndex(0);
      }, 400);
      return () => clearTimeout(pause);
    }
    const timer = setTimeout(() => setCharIndex((c) => c + 1), speed);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charIndex, paraIndex, speed, done, started]);

  useEffect(() => {
    if (done && onComplete) onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div className="respond-typewriter">
      {normalized.map((p, i) => {
        if (i > paraIndex) return null;
        const isCurrent = i === paraIndex && !done;
        const fullyTyped = i < paraIndex || done;
        const linkIndex = fullyTyped && p.link ? p.text.indexOf(p.link.text) : -1;
        const content = linkIndex >= 0
          ? <>{p.text.slice(0, linkIndex)}<Link to={p.link.to}>{p.link.text}</Link>{p.text.slice(linkIndex + p.link.text.length)}</>
          : (isCurrent ? p.text.slice(0, charIndex) : p.text);
        return (
          <p key={i} className={`respond-typewriter-line${p.emphasis ? ' respond-typewriter-line--emphasis' : ''}`}>
            {content}
            {isCurrent && <span className="respond-typewriter-cursor" />}
          </p>
        );
      })}
    </div>
  );
};

// Fades AND slides its children open/shut (rather than the instant
// pop/vanish a plain `{cond && ...}` gives, or a fade that still snaps the
// surrounding box to its new height) when `visible` flips — e.g. the
// inline per-card "we'll double check with X" note when they switch from
// Connect to Not now. Stays mounted for the duration of the CSS transition
// on the way out so it can actually animate, then unmounts. Remounts fresh
// (so TypewriterIntro retypes) if `visible` becomes true again later.
// entered is what actually drives the animated state — kept separate from
// mounted, and always starts false (even when visible is true on this
// instance's very first render), because a freshly-mounted element
// rendered directly at its open state has nothing to transition FROM (no
// previous painted frame at the closed state), so the browser just shows
// it already fully open/opaque immediately. Mounting closed first, then
// flipping entered true a beat later gives the transition a real starting
// point to animate from — so every InlineFade animates in on mount, not
// just on a later visible-flip. Uses a short setTimeout rather than
// requestAnimationFrame for that beat: rAF is tied to actual painting, so
// it can go a very long time between callbacks (or never fire) while the
// tab is backgrounded/not the active tab — confirmed directly, a 5-tick
// rAF loop timed out after 45s in a backgrounded tab. setTimeout keeps
// firing (macrotask, not paint-gated) regardless of tab visibility.
// The height slide uses grid-template-rows 0fr->1fr rather than measuring
// scrollHeight in JS — an animatable CSS-only technique that needs no
// ResizeObserver and keeps working if the content's own height changes
// (e.g. text reflow) mid-transition. The inner div supplies the single
// grid row's content height and clips it while collapsed.
const FADE_OUT_MS = 1200;
const InlineFade = ({ visible, children }) => {
  const [mounted, setMounted] = useState(visible);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (visible) {
      setMounted(true);
      const enterTimer = setTimeout(() => setEntered(true), 20);
      return () => clearTimeout(enterTimer);
    }
    setEntered(false);
    const t = setTimeout(() => setMounted(false), FADE_OUT_MS);
    return () => clearTimeout(t);
  }, [visible]);
  if (!mounted) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: entered ? '1fr' : '0fr',
        opacity: entered ? 1 : 0,
        transition: `grid-template-rows ${FADE_OUT_MS}ms ease, opacity ${FADE_OUT_MS}ms ease`,
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
};

// Public, no-login page a recipient lands on after clicking the one link
// in their suggestion email — shows every person suggested to them in that
// round together, with real interactive buttons (unlike the email itself,
// which can't run JS and so can't visually react to a click). Uses the
// app's own house-style .button/.buttonSecondary classes (see index.css)
// rather than custom button CSS, and the same card/photo-lightbox pattern
// as the admin Community Members view. Backed by
// backend/routes/suggestionResponse.js.
// ?simulate=v1|v2|v3|v7|v8 (optionally &community=<id>) — shows that page
// version with sample people so it can be tried from admin without a real
// email link. Nothing is saved: clicks only change what's on screen.
const SIMULATE_VERSIONS = ['v1', 'v2', 'v3', 'v7', 'v8', 'v9'];
const sampleAvatar = (initial, color) => `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="400" height="400" fill="${color}"/><text x="50%" y="54%" font-family="Arial, sans-serif" font-size="170" fill="#fff" text-anchor="middle" dominant-baseline="middle">${initial}</text></svg>`
)}`;
const SAMPLE_PEOPLE = [
  { userId: 'sample-1', firstName: 'Henry', age: 36, gender: 'Man', location: 'Hackney', bio: 'Back into it after a long break. Keen to meet friendly people and get out more at weekends.', lookingFor: 'New friends, Weekend plans', color: '#0ea5e9' },
  { userId: 'sample-2', firstName: 'Charlotte', age: 42, gender: 'Woman', location: 'Islington', bio: 'Loves exhibitions, long walks and good coffee. Always up for trying something new.', lookingFor: 'Activity partners, General chat', color: '#f97316' },
  { userId: 'sample-3', firstName: 'Sam', age: 29, gender: 'Man', location: 'Camden', bio: 'Moved here last year and still finding my people. Happy to join a group or meet one-to-one.', lookingFor: 'New friends', color: '#8b5cf6' },
].map(({ color, ...p }) => ({ ...p, photos: [sampleAvatar(p.firstName[0], color)], discipline: '', experienceLevel: '', myStatus: null }));

const buildSimulatedContext = (version, community) => ({
  recipientFirstName: 'Alex',
  communityName: community?.title || 'Your Community',
  communityImage: community?.image || '',
  communityImages: community?.images || [],
  respondPageVersion: version,
  suggestedPeople: SAMPLE_PEOPLE,
  showGroupQuestion: SAMPLE_PEOPLE.length >= 2,
  existingGroupAnswer: null,
});

const RespondToSuggestion = () => {
  const [searchParams] = useSearchParams();
  const simulate = SIMULATE_VERSIONS.includes(searchParams.get('simulate')) ? searchParams.get('simulate') : null;
  const simulateCommunityId = searchParams.get('community');
  const token = simulate ? null : searchParams.get('token');
  // Sentinel at the very bottom of this page's own content — see
  // scrollToBottomSmoothly's comment on why document.body itself isn't safe
  // to measure (it also includes the site navbar above this page).
  const contentEndRef = useRef(null);

  // v3 only — has this recipient already sat through the typed reveal once
  // this session (e.g. they clicked away to check their profile and came
  // back)? If so, every TypewriterIntro below renders fully typed
  // immediately instead of animating again, and the pacing pauses between
  // stages collapse to ~0. Keyed by the token itself (stable per
  // recipient+round), not roundId alone, since the token is what's actually
  // in the URL on both visits.
  const skipAnimations = (() => {
    try { return token ? sessionStorage.getItem(`respond-seen-${token}`) === '1' : false; } catch { return false; }
  })();
  const stagePause = (ms) => (skipAnimations ? 0 : ms);

  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [actionState, setActionState] = useState({});
  const [groupAnswer, setGroupAnswer] = useState(null);
  // v3's inline tickboxes (see renderPersonCard's showGroupQuestion) must
  // never render pre-ticked on first paint, even if this token already has
  // a saved existingGroupAnswer from a previous visit or from the v1/v2
  // static group card further down — that answer may not even correspond
  // to the current set of picks. Tracks whether THIS visit has made a live
  // respondGroup click, independent of groupAnswer's own value (which is
  // also seeded from the server fetch below).
  const [groupAnswerClickedLive, setGroupAnswerClickedLive] = useState(false);
  // v8 only: cards that have had their OWN group-question tick box
  // clicked — not just any card, since groupAnswer is one shared value.
  // A card whose question appears after the shared answer was already
  // set by an earlier card's click still shows its own fresh, unchecked
  // tick-box options rather than skipping straight to the confirmed
  // text — it only shows confirmed once THIS card's tick box has itself
  // been clicked.
  const [groupConfirmedIds, setGroupConfirmedIds] = useState(() => new Set());
  // Each card's OWN answer at the moment IT was confirmed — display only
  // reads this, never the shared groupAnswer, so an already-confirmed
  // card's wording ("Group"/"Individual intro preferred") never flips
  // just because a later card picked differently. The backend still only
  // ever stores one wantsGroup value per round (last write wins — see
  // groupMatchingConsentModel.js), so this is purely a display fix, not
  // a claim that the backend records a different answer per person.
  const [groupAnswerByCardId, setGroupAnswerByCardId] = useState(() => new Map());
  const [busyKey, setBusyKey] = useState(null);
  const [actionError, setActionError] = useState('');
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [closeClicked, setCloseClicked] = useState(false);
  // Delays actually swapping to the "you're done" panel until the main
  // content has had time to fade out — so clicking "Close window" reads as
  // one view fading to the next, not an instant cut.
  const [showClosePanel, setShowClosePanel] = useState(false);
  const CLOSE_FADE_MS = 400;
  useEffect(() => {
    if (!closeClicked) { setShowClosePanel(false); return undefined; }
    const t = setTimeout(() => setShowClosePanel(true), CLOSE_FADE_MS);
    return () => clearTimeout(t);
  }, [closeClicked]);
  // The main reveal above can leave the page scrolled a good way down (the
  // v3 typewriter reveal auto-scrolls as it goes, and a long list of
  // suggested people scrolls further still) — without this, swapping to the
  // much-shorter closing panel keeps that same scroll offset, landing the
  // recipient somewhere mid-page (or past its end) instead of at the top.
  useEffect(() => {
    if (!showClosePanel) return;
    setScrollTop(0, 'auto');
  }, [showClosePanel]);
  // v3 only — sequences the whole reveal: wait, type "Hi/Thanks for letting
  // us know...", fade in the person they already said yes to, type the
  // "others have also expressed interest..." + contact-details lines, fade
  // in everyone else as a staggered column, then type the closing note.
  // 'delay' -> 'typing1' -> 'leadCard' -> 'typing2' -> 'restCards' -> 'closing'
  const [v3Stage, setV3Stage] = useState('delay');
  const v3StageIndex = V3_STAGE_ORDER.indexOf(v3Stage);
  const v3StageAtLeast = (stage) => v3StageIndex >= V3_STAGE_ORDER.indexOf(stage);
  // Set once the closing note finishes typing — the Done button/note stays
  // hidden until then, so it's genuinely the last thing to appear.
  const [v3ClosingTypingDone, setV3ClosingTypingDone] = useState(false);
  // Set once the trailing "P.S...." note finishes — the Done section stays
  // hidden until then, so it can't appear while text above it is still
  // mid-type.
  const [v3FinalNoteTypingDone, setV3FinalNoteTypingDone] = useState(false);
  // Duplicates the real `leadPeople` derivation further down (can't reuse
  // it directly — hooks must run unconditionally, before the `if
  // (!context) return` guard that the real one sits after). Kept minimal
  // and independent on purpose so it can't accidentally diverge from what
  // it feeds into: how many lead cards exist to reveal, one at a time.
  const v3ArrivedAlreadyInterestedForReveal = !!(context && context.respondPageVersion !== 'v1'
    && context.suggestedPeople.some((p) => p.myStatus === 'interested' && p.firstName));
  const v3LeadPeopleForReveal = (context && context.respondPageVersion === 'v3' && v3ArrivedAlreadyInterestedForReveal)
    ? context.suggestedPeople.filter((p) => p.myStatus === 'interested')
    : [];
  // Same duplication reasoning as v3LeadPeopleForReveal above — mirrors the
  // real `restPeople` derivation further down.
  const v3RestPeopleForReveal = context && context.respondPageVersion === 'v3'
    ? (v3ArrivedAlreadyInterestedForReveal
      ? context.suggestedPeople.filter((p) => p.myStatus !== 'interested')
      : context.suggestedPeople)
    : [];
  // Reveals leadPeople one card at a time (not all at once) — each one gets
  // its own scroll-into-view moment, then a pause before its Connect button
  // flips to the selected "✓ Connect" state (already recorded server-side
  // from the email's own Connect click, but hidden until now — see
  // renderPersonCard's `confirmed`), before the next card appears.
  // leadRevealedCount is how many cards are currently mounted;
  // leadConfirmedIds is which of those have had their button flip.
  const [leadRevealedCount, setLeadRevealedCount] = useState(0);
  const [leadConfirmedIds, setLeadConfirmedIds] = useState(() => new Set());
  // Same one-at-a-time reveal for the rest-of-cards column (no confirm step
  // needed here — these people's status was never hidden to begin with).
  const [restRevealedCount, setRestRevealedCount] = useState(0);
  // Which cards' inline "we'll double check with X..." message (see
  // renderPersonCard's showInlineThanks) has finished typing — the reveal
  // sequences below wait for this before showing the next card, so the
  // typewriter never gets cut off by the next card mounting underneath it.
  const [thanksTypedIds, setThanksTypedIds] = useState(() => new Set());
  // Which cards' inline thanks message should use the short "We'll update
  // soon." wording instead of the normal "Thanks, we'll double check with
  // X..." one — decided once, at the moment THIS click resolves (see
  // respond() below), based on whether someone else was already interested
  // right before it. Deliberately NOT derived live from interestedCount at
  // render time: that would make an earlier card's already-typed message
  // retroactively swap to the short wording the moment a later, unrelated
  // pick pushes the total to 2+ — every render re-evaluates the ternary
  // that picks TypewriterIntro's `paragraphs`, and once `done` is true it
  // renders whatever that prop currently says, no retyping needed to make
  // stale text visibly change. This set is add-only, so once a card's
  // wording is decided it can only be revisited by a fresh click on that
  // same card (Not now, then Connect again), never by anyone else's click.
  const [multiSelectClickIds, setMultiSelectClickIds] = useState(() => new Set());
  // v8 only: cards whose group-question section ("Also, would you prefer
  // to be introduced:") is allowed to appear. Staggered a beat behind the
  // "Interconnecting: ..." thanks text right above it (see the effect
  // below) rather than popping in at the same instant, so the two reveal
  // as a sequence rather than a single simultaneous block.
  const [groupSectionRevealedIds, setGroupSectionRevealedIds] = useState(() => new Set());
  // v8 only: cards where the recipient clicked the discreet "Edit choice"
  // link to bring the Interested/Not now buttons back after saying Not
  // now. Only offered from Not now, never from Interested — undoing an
  // Interested click could be unwinding something else already triggered
  // by it (a mutual-match, the other person notified), whereas Not now
  // has no such downstream effect to unwind. Cleared for a card the
  // moment a new choice is actually submitted for it (see respond()),
  // so it falls back to the normal confirmation display for whatever the
  // new status is.
  const [editingIds, setEditingIds] = useState(() => new Set());
  // Which cards' inline group-chat/1:1 question (see showGroupQuestion) has
  // finished typing — its two options stay hidden (then fade in, staggered
  // one after the other) until this flips true, rather than appearing
  // alongside the question mid-type.
  const [groupQuestionTypedIds, setGroupQuestionTypedIds] = useState(() => new Set());
  // Which cards have finished loading all of their own photos — the reveal
  // sequences below also wait for this, so a card that's visually mounted
  // (CSS fade-in done) but whose actual photo(s) are still downloading over
  // the network doesn't get treated as "fully appeared" yet.
  const [photosReadyIds, setPhotosReadyIds] = useState(() => new Set());
  const photoLoadCountsRef = useRef({});
  const markPhotoLoaded = useCallback((userId, total) => {
    photoLoadCountsRef.current[userId] = (photoLoadCountsRef.current[userId] || 0) + 1;
    if (photoLoadCountsRef.current[userId] >= total) {
      setPhotosReadyIds((prev) => (prev.has(userId) ? prev : new Set(prev).add(userId)));
    }
  }, []);
  const isCardPhotosReady = (person) => !person || !person.photos || person.photos.length === 0 || photosReadyIds.has(person.userId);

  useEffect(() => {
    if (!context || context.respondPageVersion !== 'v3' || v3Stage !== 'delay') return undefined;
    const t = setTimeout(() => setV3Stage('typing1'), stagePause(500));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, v3Stage]);

  // Kicks off the lead-card sequence as soon as that stage is entered — if
  // there's nothing to reveal (arrived without any pre-existing interest),
  // skips straight through to typing2 instead of stalling here forever.
  useEffect(() => {
    if (v3Stage !== 'leadCard') return undefined;
    if (v3LeadPeopleForReveal.length === 0) { setV3Stage('typing2'); return undefined; }
    if (leadRevealedCount === 0) setLeadRevealedCount(1);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v3Stage]);

  // Scroll moment #1 (one of exactly two per major beat) — fires fresh
  // every time one more lead card mounts, not just once for the whole
  // block, since each one appearing is its own moment worth following.
  // Skipped entirely on a repeat view (skipAnimations) — that case gets
  // its own single, purposeful scroll instead (see further below), rather
  // than replaying every one of these incremental follows in a fraction of
  // a second.
  useEffect(() => {
    if (skipAnimations || v3Stage !== 'leadCard' || leadRevealedCount === 0) return undefined;
    scrollToBottomSmoothly(contentEndRef);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadRevealedCount]);

  // ~0.9s after a card appears, flip its Connect button to selected.
  useEffect(() => {
    if (v3Stage !== 'leadCard' || leadRevealedCount === 0) return undefined;
    const current = v3LeadPeopleForReveal[leadRevealedCount - 1];
    if (!current || leadConfirmedIds.has(current.userId)) return undefined;
    const t = setTimeout(() => {
      setLeadConfirmedIds((prev) => new Set(prev).add(current.userId));
    }, stagePause(900));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadRevealedCount, v3Stage]);

  // Once that card's button has flipped AND its inline "we'll double
  // check..." message has actually finished typing (not just started —
  // leadConfirmedIds flips the button and reveals the message, but the
  // typewriter itself still takes another second or so), reveal the next
  // one, or — if it was the last — move on to typing2. Scroll moment #2
  // for this beat happens naturally when v3Stage changes, via the effect
  // below.
  useEffect(() => {
    if (v3Stage !== 'leadCard' || leadRevealedCount === 0) return undefined;
    const current = v3LeadPeopleForReveal[leadRevealedCount - 1];
    if (!current || !leadConfirmedIds.has(current.userId) || !thanksTypedIds.has(current.userId)) return undefined;
    if (!isCardPhotosReady(current)) return undefined;
    const t = setTimeout(() => {
      if (leadRevealedCount < v3LeadPeopleForReveal.length) setLeadRevealedCount((n) => n + 1);
      else setV3Stage('typing2');
    }, stagePause(400));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadConfirmedIds, thanksTypedIds, v3Stage, photosReadyIds]);

  // Scroll moment #2 — the beat just before the next block of text starts,
  // covering every stage transition that isn't itself a lead-card reveal (a
  // card block or the final Done section growing the page independently of
  // any typing). Skipped on a repeat view — see the note above.
  useEffect(() => {
    if (skipAnimations || !context || context.respondPageVersion !== 'v3') return undefined;
    scrollToBottomSmoothly(contentEndRef);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, v3Stage, v3ClosingTypingDone, v3FinalNoteTypingDone]);

  // Repeat-view only: no incremental following — either land straight on
  // whichever suggested person hasn't been responded to yet (top of
  // viewport, so it's immediately actionable), or don't scroll at all if
  // everyone's already been responded to.
  useEffect(() => {
    if (!skipAnimations || !v3FinalNoteTypingDone || !context) return;
    const unresponded = (context.suggestedPeople || []).find((p) => !actionState[p.userId]);
    if (!unresponded) return;
    document.getElementById(`respond-person-${unresponded.userId}`)?.scrollIntoView({ block: 'start', behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipAnimations, v3FinalNoteTypingDone]);

  // Marks this token's reveal as fully seen once the very last typed note
  // finishes — skipAnimations reads this back on the next mount (e.g. after
  // clicking away to ProfilePreview and back), so it renders instantly
  // instead of replaying.
  useEffect(() => {
    if (!v3FinalNoteTypingDone || !token) return;
    try { sessionStorage.setItem(`respond-seen-${token}`, '1'); } catch { /* ignore */ }
  }, [v3FinalNoteTypingDone, token]);

  // Kicks off the rest-of-cards sequence as soon as that stage is entered —
  // same one-at-a-time pattern as the lead cards above, just without a
  // confirm step (these people's status was never hidden). If there's
  // nothing to reveal, skips straight to the closing note.
  useEffect(() => {
    if (v3Stage !== 'restCards') return undefined;
    if (v3RestPeopleForReveal.length === 0) { setV3Stage('closing'); return undefined; }
    if (restRevealedCount === 0) setRestRevealedCount(1);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v3Stage]);

  // Scroll moment — fires fresh every time one more rest-of-cards card
  // mounts, so the closing note can never start typing before the last one
  // has actually appeared.
  useEffect(() => {
    if (skipAnimations || v3Stage !== 'restCards' || restRevealedCount === 0) return undefined;
    scrollToBottomSmoothly(contentEndRef);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restRevealedCount]);

  // After a short pause, reveal the next card — or, once the last one has
  // had its own beat to be seen, move on to the closing note. If they've
  // clicked Connect on the current card, its inline "we'll double
  // check..." message needs to actually finish typing first — otherwise
  // (Not now, or no decision yet) there's nothing typing, so the pause
  // alone is enough (doesn't block indefinitely on an undecided card).
  useEffect(() => {
    if (v3Stage !== 'restCards' || restRevealedCount === 0) return undefined;
    const current = v3RestPeopleForReveal[restRevealedCount - 1];
    if (current && actionState[current.userId] === 'interested' && !thanksTypedIds.has(current.userId)) return undefined;
    if (!isCardPhotosReady(current)) return undefined;
    const t = setTimeout(() => {
      if (restRevealedCount < v3RestPeopleForReveal.length) setRestRevealedCount((n) => n + 1);
      else setV3Stage('closing');
    }, stagePause(900));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restRevealedCount, v3Stage, actionState, thanksTypedIds, photosReadyIds]);

  useEffect(() => {
    if (!simulate) return undefined;
    let cancelled = false;
    (async () => {
      let community = null;
      if (simulateCommunityId) {
        try {
          const res = await fetch(`${API}/communities/${encodeURIComponent(simulateCommunityId)}`);
          if (res.ok) {
            const c = await res.json();
            const pics = Array.isArray(c.pics) ? c.pics : String(c.pics || '').split(',').filter(Boolean);
            community = { title: c.title, image: pics[0] || '', images: pics.slice(0, 6) };
          }
        } catch { /* sample community name instead */ }
      }
      if (!cancelled) setContext(buildSimulatedContext(simulate, community));
    })();
    return () => { cancelled = true; };
  }, [simulate, simulateCommunityId]);

  useEffect(() => {
    if (simulate) return undefined;
    if (!token) { setError('This link looks incomplete — please use the link from your email directly.'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/respond/context?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) { setError(data.error || 'This link is invalid or has expired.'); return; }
        setContext(data);
        setGroupAnswer(data.existingGroupAnswer);
        const seeded = {};
        (data.suggestedPeople || []).forEach((p) => { if (p.myStatus) seeded[p.userId] = p.myStatus; });
        setActionState(seeded);
        // v8 only: seed the per-card group tracking (groupConfirmedIds/
        // groupAnswerByCardId) from data.existingGroupAnswer so a reload
        // doesn't re-ask the group question on every already-interested
        // card. This is a real limitation, not a full fix — the backend
        // (groupMatchingConsentModel.js) only ever stores ONE wantsGroup
        // value per round, not a separate answer per partner, so on
        // reload every already-interested card is seeded with that SAME
        // single value rather than whichever distinct answer it actually
        // got during the live session that set it (per-card divergence,
        // e.g. John=group/Jack=individual, only exists live in this
        // component's own state and doesn't survive a reload).
        if (data.existingGroupAnswer !== null && data.existingGroupAnswer !== undefined) {
          const interestedIds = (data.suggestedPeople || [])
            .filter((p) => p.myStatus === 'interested')
            .map((p) => p.userId);
          setGroupConfirmedIds(new Set(interestedIds));
          setGroupAnswerByCardId(new Map(interestedIds.map((id) => [id, data.existingGroupAnswer])));
        }
      } catch (err) {
        if (!cancelled) setError('Something went wrong loading this page — please try again shortly.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const respond = useCallback(async (partnerUserId, action) => {
    if (actionState[partnerUserId] === action) return;
    const key = `${partnerUserId}:${action}`;
    setBusyKey(key);
    setActionError('');
    try {
      if (!simulate) {
        const res = await fetch(`${API}/respond/action`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, partnerUserId, action }),
        });
        const data = await res.json();
        if (!res.ok) { setActionError(data.error || 'Could not record your response.'); return; }
      }
      if (action === 'interested') {
        const alreadyInterestedBeforeThis = Object.values(actionState).filter((v) => v === 'interested').length;
        if (alreadyInterestedBeforeThis >= 1) {
          setMultiSelectClickIds((prev) => (prev.has(partnerUserId) ? prev : new Set(prev).add(partnerUserId)));
        }
      }
      setActionState((prev) => ({ ...prev, [partnerUserId]: action }));
      setEditingIds((prev) => (prev.has(partnerUserId) ? (() => { const next = new Set(prev); next.delete(partnerUserId); return next; })() : prev));
    } catch (err) {
      setActionError('Could not record your response — please try again.');
    } finally {
      setBusyKey(null);
    }
  }, [token, actionState, simulate]);

  const respondGroup = useCallback(async (answer, cardUserId) => {
    const key = `group:${answer}`;
    setBusyKey(key);
    setActionError('');
    // Who this answer actually applies to — everyone currently settled as
    // interested (see settledInterestedIds further down; recomputed here
    // rather than shared directly since this callback, a hook, is declared
    // before that plain-render derivation). Sent along so the backend can
    // record exactly which people this "yes" was about, not just a bare
    // yes/no — see groupMatchingConsentModel.js's groupUserIds.
    const groupUserIds = [...thanksTypedIds].filter((id) => actionState[id] === 'interested');
    try {
      if (!simulate) {
        const res = await fetch(`${API}/respond/group`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, answer, groupUserIds }),
        });
        const data = await res.json();
        if (!res.ok) { setActionError(data.error || 'Could not record your response.'); return; }
      }
      setGroupAnswer(answer === 'yes');
      setGroupAnswerClickedLive(true);
      if (cardUserId) {
        setGroupConfirmedIds((prev) => new Set(prev).add(cardUserId));
        setGroupAnswerByCardId((prev) => new Map(prev).set(cardUserId, answer === 'yes'));
      }
    } catch (err) {
      setActionError('Could not record your response — please try again.');
    } finally {
      setBusyKey(null);
    }
  }, [token, thanksTypedIds, actionState, simulate]);

  // v8 only: reveals the group-question section only after a card's own
  // "Interconnecting: ..." thanks text has FULLY finished fading in,
  // rather than both popping in at the same instant. Delay equals
  // FADE_OUT_MS (not some shorter stagger) — InlineFade's fade-in takes
  // that long, so a shorter delay would start the group question's own
  // fade-in while the thanks text was still mid-fade, visually blending
  // the two instead of reading as a clear first-this-then-that sequence.
  // Every interested card qualifies (including the very first pick, not
  // just the second-onward — v8 asks 1:1-vs-group per person from the
  // start), unlike multiSelectClickIds, which stays scoped to "second
  // pick onward" for its own separate purpose (shortening a card's
  // thanks message). context?.respondPageVersion, not the isV8 const
  // further down — this effect is registered before that const's
  // declaration (has to be, hooks can't come after the loading/error
  // early-returns), so referencing isV8 here would throw on any render
  // that hits those early returns before ever assigning it.
  useEffect(() => {
    if (context?.respondPageVersion !== 'v8' && context?.respondPageVersion !== 'v9') return undefined;
    const newIds = Object.keys(actionState).filter(
      (id) => actionState[id] === 'interested' && !groupSectionRevealedIds.has(id)
    );
    if (newIds.length === 0) return undefined;
    const t = setTimeout(() => {
      setGroupSectionRevealedIds((prev) => new Set([...prev, ...newIds]));
    }, FADE_OUT_MS);
    return () => clearTimeout(t);
  }, [context, actionState, groupSectionRevealedIds]);

  if (error) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-card respond-card--message">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!context) {
    // Spinner shows instantly on first paint (no delay/fade-in) rather than
    // a blank page while /respond/context is in flight — the fetch can take
    // a couple of seconds and an unbroken blank screen reads as broken.
    return (
      <div className="respond-page">
        <div className="respond-loading">
          <div className="respond-spinner" />
        </div>
      </div>
    );
  }

  const joinNames = (names) => (
    names.length === 0 ? '' : names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  );

  const interestedCount = Object.values(actionState).filter((v) => v === 'interested').length;
  // Drives the closing panel's wording — actionState is seeded from
  // server-side myStatus at load (so this is true for anyone who arrived
  // having already clicked Connect in the email, not just fresh clicks
  // made on this visit).
  const hasResponded = Object.values(actionState).some((v) => v === 'interested' || v === 'not_interested');
  // Closing-panel-only — the community's own gallery pics, falling back to
  // its single hero image if it has no gallery set, purely decorative
  // fodder for the little spin-into-the-portal effect.
  const closePanelPhotos = (
    context.communityImages?.length > 0 ? context.communityImages : [context.communityImage].filter(Boolean)
  ).slice(0, 6);
  const interestedNames = context.suggestedPeople
    .filter((p) => actionState[p.userId] === 'interested')
    .map((p) => p.firstName)
    .filter(Boolean);
  const interestedNamesText = interestedNames.length === 0 ? 'they' : joinNames(interestedNames);
  const keenToConnectText = context.suggestedPeople.length === 1
    ? `${context.suggestedPeople[0].firstName || 'They'} is keen to connect in ${context.communityName || 'your community'}.`
    : `These people are keen to connect in ${context.communityName || 'your community'}.`;
  // v7 only — always everyone suggested this round, regardless of any
  // arrival/interested state (v7 has no "already interested" branch).
  // "and or" (not just "and") between the last two names, deliberately —
  // v7's own copy spec, distinct from joinNames' usual "and" used
  // everywhere else on this page.
  const allSuggestedNames = context.suggestedPeople.map((p) => p.firstName).filter(Boolean);
  const allSuggestedNamesText = allSuggestedNames.length <= 1
    ? (allSuggestedNames[0] || '')
    : `${allSuggestedNames.slice(0, -1).join(', ')} and or ${allSuggestedNames[allSuggestedNames.length - 1]}`;

  // v2 (default): if they arrived here via the one-click "Connect" button
  // in the email itself (server already recorded that partner's myStatus
  // as 'interested' before this page ever loaded — distinct from
  // actionState, which only reflects clicks made on THIS page), greet them
  // acknowledging that instead of asking the generic intro question, and
  // point them at whoever else was suggested this round. v1: always the
  // original generic copy, regardless of arrival state. v3: same
  // acknowledge-then-ask logic as v2, but typed out (see TypewriterIntro)
  // and naming exactly who's left to answer. All admin-chosen per round
  // (see suggestionResponseToken.js), so any of the three can be compared.
  const isV1 = context.respondPageVersion === 'v1';
  const isV3 = context.respondPageVersion === 'v3';
  // v7: no typed reveal, always the plain "Click connect below..." wording
  // regardless of arrival state (like v1), the same per-card anchored
  // group question as v3 (not v1/v2's single end-of-page one), and ends
  // with a bare "Thanks!" — no Close window section at all.
  const isV7 = context.respondPageVersion === 'v7';
  // v8: identical mechanics to v7 (no typed reveal, no arrived-already
  // branch, inline-only group question, plain "Thanks!" ending, no Close
  // window section) — the only difference is the header copy itself,
  // which points at the community rather than naming who was suggested
  // (see the isV8 branch further down, and keenToConnectText above).
  // v9 is v8 in every way except its header: it invites them to say who
  // they'd like to connect with, rather than saying these people are keen.
  const isV9 = context.respondPageVersion === 'v9';
  const isV8 = context.respondPageVersion === 'v8' || isV9;
  const arrivedInterestedNames = context.suggestedPeople
    .filter((p) => p.myStatus === 'interested')
    .map((p) => p.firstName)
    .filter(Boolean);
  const arrivedAlreadyInterested = !isV1 && !isV7 && !isV8 && arrivedInterestedNames.length > 0;
  const remainingNames = context.suggestedPeople
    .filter((p) => p.myStatus !== 'interested')
    .map((p) => p.firstName)
    .filter(Boolean);
  const othersRemaining = remainingNames.length > 0;

  // v3's reveal is split into two typed phases with the lead card's fade-in
  // sandwiched between them (see v3Stage above) — phase 1 is the greeting
  // + acknowledgement, phase 2 is "who else is interested" + the
  // contact-details reassurance. When they didn't arrive pre-interested
  // (v1/v2's fallback case too), phase 1 covers the whole generic intro and
  // phase 2 is just the contact-details line, with every suggested person
  // treated as "rest" (no separate lead card to sandwich).
  const v3Greeting = { text: `Hi ${context.recipientFirstName || 'there'}!`, emphasis: true };
  const v3IntroParagraphs1 = arrivedAlreadyInterested
    ? [v3Greeting, `Thanks for letting us know you'd like to connect with ${joinNames(arrivedInterestedNames)} :)`]
    : [v3Greeting, keenToConnectText, 'Would you like us to put you in touch?'];
  const v3IntroParagraphs2 = arrivedAlreadyInterested && othersRemaining
    ? [`Also, let us know if you'd like to connect with ${joinNames(remainingNames)}. Just click, connect...`]
    : [];

  // Typed after the rest-of-cards column has appeared (not before it, like
  // v3IntroParagraphs2) — the contact-details reassurance plus a closing
  // note pointing back at their own profile, with "click here" swapped for
  // a real link once that line finishes typing (see TypewriterIntro).
  const v3ClosingParagraphs = [
    'We won\'t share your contact details (we intro you over messenger).',
  ];

  const leadPeople = isV3 && arrivedAlreadyInterested ? context.suggestedPeople.filter((p) => p.myStatus === 'interested') : [];
  const restPeople = isV3 && arrivedAlreadyInterested ? context.suggestedPeople.filter((p) => p.myStatus !== 'interested') : context.suggestedPeople;

  // v3/v7 only — who's currently confirmed interested, in the order each
  // one's own inline "we'll double check with X" note finished typing
  // (thanksTypedIds is a Set, which iterates in insertion order) — that's
  // also the order they were effectively settled on, whether via the
  // email's own Connect click (the lead card, auto-confirmed on load) or a
  // live click on this page. Filtered against actionState so someone who
  // was interested and then switched to "Not now" drops out immediately.
  // Every card from the SECOND pick onward gets its own group-chat/1:1
  // question right under it (see renderPersonCard's use of
  // multiSelectClickIds) — not anchored to just one card — naming everyone
  // settled so far. v3/v7 gate this on thanksTypedIds (only "settled" once
  // that card's own typed thanks animation finishes); v8 has no typing
  // animation for its thanks text (see renderPersonCard's isV8 branch,
  // which never populates thanksTypedIds), so a pick counts as settled
  // immediately there instead.
  const settledInterestedIds = isV8
    ? context.suggestedPeople.filter((p) => actionState[p.userId] === 'interested').map((p) => p.userId)
    : [...thanksTypedIds].filter((id) => actionState[id] === 'interested');
  const settledInterestedPeople = settledInterestedIds
    .map((id) => context.suggestedPeople.find((p) => p.userId === id))
    .filter(Boolean);
  const settledInterestedNamesText = joinNames(settledInterestedPeople.map((p) => p.firstName).filter(Boolean));

  const renderPersonCard = (p, extra = {}) => {
    // extra.pendingSelect: this card's own real status (already
    // 'interested' server-side, from the email's Connect click) is
    // deliberately hidden until extra.confirmed flips true — see the
    // per-card reveal effects above.
    const status = extra.pendingSelect && !extra.confirmed ? null : actionState[p.userId];
    // v8 only: this card's own "In a group (with X, Y)" list — computed
    // per card rather than reusing extra.groupQuestionNames/People (the
    // same list for every card, per settledInterestedPeople above), and
    // requires groupAnswerByCardId.get(gp.userId) === true specifically
    // — i.e. that OTHER person must have themselves already picked
    // "group" too, not merely be marked interested. Being interested in
    // someone doesn't mean they've agreed to any group; showing them in
    // the list before they'd made their own choice read as "X has agreed
    // to a group with you", which wasn't true yet. v3/v7 keep using the
    // shared extra.groupQuestionNames/People as before (their own tuned
    // behavior, never asked per-person from the first pick the way v8
    // now is).
    const groupQuestionPeopleForCard = isV8
      ? settledInterestedPeople.filter((gp) => gp.userId !== p.userId && groupAnswerByCardId.get(gp.userId) === true)
      : extra.groupQuestionPeople;
    const groupQuestionNamesForCard = isV8
      ? joinNames(groupQuestionPeopleForCard.map((gp) => gp.firstName).filter(Boolean))
      : extra.groupQuestionNames;
    return (
      <div id={`respond-person-${p.userId}`} className={`respond-card${extra.className ? ` ${extra.className}` : ''}`} style={extra.style}>
        <div className="respond-photo-gallery">
          {p.photos && p.photos.length > 0 ? (
            p.photos.map((photo, i) => (
              <img
                key={i}
                src={photo}
                alt=""
                className={`respond-img respond-img--clickable${i === 0 ? ' respond-img--lead' : ''}`}
                onClick={() => setEnlargedPhoto(photo)}
                onLoad={() => markPhotoLoaded(p.userId, p.photos.length)}
                onError={() => markPhotoLoaded(p.userId, p.photos.length)}
              />
            ))
          ) : (
            <div className="respond-img-placeholder">?</div>
          )}
        </div>
        <div className="respond-details">
          <div className="respond-headline">
            <strong>{p.firstName || 'Someone'}</strong>
            {p.age != null && <span className="respond-age">{p.age}</span>}
            {p.gender && <span className="respond-meta"> · {p.gender}</span>}
          </div>
          {p.location && <div className="respond-meta">{p.location}</div>}
          {p.discipline && <div className="respond-meta"><strong>Discipline:</strong> {p.discipline}</div>}
          {p.experienceLevel && <div className="respond-meta"><strong>Experience Level:</strong> {p.experienceLevel}</div>}
          {p.lookingFor && <div className="respond-meta"><strong>Looking For:</strong> {p.lookingFor}</div>}
          {p.bio && <div className="respond-bio">{p.bio}</div>}
          {/* v8 only: once a choice is made, the buttons themselves go
              away rather than just changing style — the inline thanks
              text (interested) or the plain acknowledgement below
              (not_interested) takes their place instead of sitting
              alongside them. v1/v2/v3/v7 keep the buttons visible
              (their own already-tuned behavior), so this is gated on
              isV8 specifically rather than changing shared layout.
              editingIds brings the buttons back after a Not now + "Edit
              choice" click, so the recipient can pick again. */}
          <InlineFade visible={!(isV8 && status) || editingIds.has(p.userId)}>
            <div className="respond-actions">
              <button
                type="button"
                className={status === 'interested' ? 'button' : 'buttonSecondary'}
                disabled={busyKey === `${p.userId}:interested`}
                onClick={() => respond(p.userId, 'interested')}
              >
                {status === 'interested' ? '✓ Interested' : 'Interested'}
              </button>
              <button
                type="button"
                className={status === 'not_interested' ? 'button' : 'buttonSecondary'}
                disabled={busyKey === `${p.userId}:not_interested`}
                onClick={() => respond(p.userId, 'not_interested')}
              >
                {status === 'not_interested' ? '✓ Not now' : 'Not now'}
              </button>
            </div>
          </InlineFade>
          {/* v8 only: everything that appears once a choice is made
              (thanks text, group question) is visually boxed off from the
              profile info above it, so it reads as a distinct "your
              response" area rather than blending into the bio. Wraps the
              same blocks v3/v7 already used unwrapped — className is
              undefined for them, so the div is a plain, layout-neutral
              passthrough there (no box, no behavior change). The class
              itself (not the div's mounting) is gated on status being
              set, so this never renders as a visible empty box before
              any click — before that it's just an unstyled div. */}
          <div className={isV8 && status ? 'respond-response-box' : undefined}>
          {isV8 && (
            <InlineFade visible={status === 'not_interested' && !editingIds.has(p.userId)}>
              <div style={{ marginTop: '0.6rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b', overflowWrap: 'break-word' }}>
                  Thanks for letting us know
                </div>
                {/* Discreet — only offered from Not now, never Interested
                    (see editingIds above for why). .respond-edit-choice-link
                    (RespondToSuggestion.css) — index.css's global button
                    rules force color:#fff !important on every bare
                    <button>, so a plain inline style here would render
                    white-on-white; needs matching !important, same as
                    .respond-tick-btn just above. */}
                <button
                  type="button"
                  className="respond-edit-choice-link"
                  onClick={() => setEditingIds((prev) => new Set(prev).add(p.userId))}
                >
                  Edit choice
                </button>
              </div>
            </InlineFade>
          )}
          {/* An immediate per-card acknowledgement right where they
              clicked (or, for a lead card, right as it's confirmed),
              rather than making them scroll down to a single summary line.
              Three wordings: a lead card confirming what the email's own
              Connect click already recorded (leadThanksVariant — part of
              the page's own load-time reveal, so DOES respect
              skipAnimations); someone already-interested picking another
              person on the page (arrivedThanksVariant); or a first-ever
              click with no prior interest at all. The latter two are
              always live clicks, never part of a replay, so they never go
              instant regardless of skipAnimations. If this click was the
              2nd (or later) pick overall, the normal wording is swapped for
              a short "We'll update soon." instead — see
              multiSelectClickIds above for why that's decided once, at
              click-resolution time in respond(), rather than read live off
              interestedCount here: this ternary re-runs on every render,
              and once a message is `done` typing it renders whatever the
              current paragraph text says with no retype needed — so a
              live check would make an EARLIER, already-typed message
              retroactively flip to the short wording the moment some
              LATER, unrelated pick pushes the total to 2+. Lead cards are
              exempt: they keep their dedicated wording regardless, since
              the short combined message is about reacting to a live pick,
              not the load-time reveal. */}
          {extra.showInlineThanks && isV8 && (
            // v8 is always instant (no typewriter reveal), so this is
            // plain styled JSX rather than TypewriterIntro — lets the
            // "Interconnecting:" label be its own house-purple/bold run
            // distinct from the message text after it, which a single
            // typed plain-text paragraph couldn't express.
            <InlineFade visible={status === 'interested'}>
              <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', overflowWrap: 'break-word' }}>
                <strong style={{ color: '#4338ca' }}>Interconnecting: </strong>
                <span style={{ color: '#1e293b', fontWeight: 600 }}>
                  {!extra.leadThanksVariant && multiSelectClickIds.has(p.userId)
                    ? "We'll update soon!"
                    : `We'll be back in touch soon - we'll double check with ${p.firstName || 'them'} and then intro you over messenger.`}
                </span>
              </div>
            </InlineFade>
          )}
          {extra.showInlineThanks && !isV8 && (
            <InlineFade visible={status === 'interested'}>
              <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', fontWeight: 600, color: '#4338ca', overflowWrap: 'break-word' }}>
                <TypewriterIntro
                  paragraphs={[
                    !extra.leadThanksVariant && multiSelectClickIds.has(p.userId)
                      ? "We'll update soon!"
                      : extra.leadThanksVariant
                        ? `We'll double check with ${p.firstName || 'them'}, and then intro you over messenger`
                        : extra.arrivedThanksVariant
                          ? `We'll double check with ${p.firstName || 'them'}, and then intro you over messenger hopefully soon!`
                          : `Thanks, we'll double check with ${p.firstName || 'them'}, and then intro you over messenger.`,
                  ]}
                  instant={isV7 || (extra.leadThanksVariant ? skipAnimations : false)}
                  startDelay={200}
                  onComplete={() => setThanksTypedIds((prev) => (prev.has(p.userId) ? prev : new Set(prev).add(p.userId)))}
                />
              </div>
            </InlineFade>
          )}
          {/* v3/v7: pops up right under EVERY card from the second pick
              onward (same multiSelectClickIds set that shortens that
              card's own thanks message to "We'll update soon!") — not
              anchored to a single card. v1/v2 use their own single
              end-of-page group question further down instead.
              v8: shown under EVERY interested card, including the very
              first pick — not gated on multiSelectClickIds at all, since
              v8 asks 1:1-vs-group per person from the start (see the
              staggering effect near respondGroup, which likewise doesn't
              require multiSelectClickIds for v8). Wrapped in InlineFade
              (always rendered, visible toggled via the condition below)
              rather than a plain conditional so switching back to "Not
              now" fades it out here instead of just vanishing. */}
          <InlineFade visible={((isV3 || isV7) && multiSelectClickIds.has(p.userId) && status === 'interested') || (isV8 && groupSectionRevealedIds.has(p.userId) && status === 'interested')}>
            {/* v8, once answered: the question + tick buttons are replaced
                entirely by a single confirmation line, tight under the
                "Interconnecting: ..." thanks text above (small margin, not
                the usual 0.9rem question spacing) rather than leaving the
                buttons visible alongside it. v3/v7 keep the persistent
                buttons+checkmark behavior unchanged. groupAnswer is global
                (one answer applies everywhere this question shows), same
                as the existing checkbox-checked styling already relies on. */}
            {/* v8: same fade-out-buttons/fade-in-confirmation pairing as
                the Interested/Not now buttons above — tick buttons visible
                first, then fade to the confirmation line once answered.
                v3/v7 only ever see the question+buttons InlineFade (their
                visible is always true, since isV8 is false for them), so
                their existing persistent-buttons behavior is unchanged.
                Gated on groupConfirmedIds (this specific card's own tick
                box having been clicked), NOT just groupAnswer !== null —
                a card whose question first appears after groupAnswer was
                already set by an EARLIER card must still show its own
                fresh, unchecked tick-box options rather than skipping
                straight to this confirmed view; it only gets here once
                its own tick box is clicked too. Reads
                groupAnswerByCardId (this card's own answer at the moment
                IT was confirmed), not the shared groupAnswer — otherwise
                an already-confirmed card's wording would flip every time
                a later card picks differently. */}
            <InlineFade visible={isV8 && groupConfirmedIds.has(p.userId)}>
              <div className="respond-group-inline" style={{ marginTop: '0.3rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', overflowWrap: 'break-word' }}>
                {groupAnswerByCardId.get(p.userId) === true ? (
                  <>
                    <strong style={{ color: '#4338ca' }}>Group intro preferred</strong>
                    {groupQuestionNamesForCard && ` (with ${groupQuestionNamesForCard})`}
                    {groupQuestionPeopleForCard?.length > 0 && (
                      <span className="respond-tick-avatars" style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }}>
                        {groupQuestionPeopleForCard.filter((gp) => gp.photos?.[0]).slice(0, 4).map((gp, i) => (
                          <img key={gp.userId} src={gp.photos[0]} alt="" className="respond-tick-avatar" style={{ marginLeft: i > 0 ? '-8px' : 0, zIndex: 10 - i }} />
                        ))}
                      </span>
                    )}
                  </>
                ) : (
                  <strong style={{ color: '#4338ca' }}>Individual intro preferred.</strong>
                )}
              </div>
            </InlineFade>
            <InlineFade visible={!(isV8 && groupConfirmedIds.has(p.userId))}>
              <div className="respond-group-inline" style={{ marginTop: '0.9rem' }}>
                <TypewriterIntro
                  key={groupQuestionNamesForCard}
                  paragraphs={['Also, would you prefer to be introduced:']}
                  instant={isV7 || isV8}
                  startDelay={400}
                  onComplete={() => setGroupQuestionTypedIds((prev) => (prev.has(p.userId) ? prev : new Set(prev).add(p.userId)))}
                />
                {groupQuestionTypedIds.has(p.userId) && (
                  <div className="respond-actions respond-actions--sub" style={{ marginTop: '0.6rem' }}>
                    <button
                      type="button"
                      className="respond-tick-btn respond-fade-in"
                      style={{ animationDelay: '0s', animationFillMode: 'backwards' }}
                      disabled={busyKey === 'group:no'}
                      onClick={() => respondGroup('no', p.userId)}
                    >
                      {/* isV8 uses groupConfirmedIds + groupAnswerByCardId
                          (this card's own click and own answer), not the
                          shared groupAnswerClickedLive/groupAnswer — a
                          later card's tick boxes must render
                          fresh/unchecked even though someone else's card
                          already answered differently. v3/v7 keep
                          groupAnswerClickedLive/groupAnswer unchanged (only
                          ever one occurrence of this question for them). */}
                      <span className={`respond-tickbox${(isV8 ? groupConfirmedIds.has(p.userId) && groupAnswerByCardId.get(p.userId) === false : groupAnswerClickedLive && groupAnswer === false) ? ' respond-tickbox--checked' : ''}`} />
                      {p.photos?.[0] && <img src={p.photos[0]} alt="" className="respond-tick-avatar" />}
                      <span className="respond-tick-label">{isV8 ? 'Individually' : '1:1 Individually'}</span>
                    </button>
                    <button
                      type="button"
                      className="respond-tick-btn respond-fade-in"
                      style={{ animationDelay: '0.3s', animationFillMode: 'backwards' }}
                      disabled={busyKey === 'group:yes'}
                      onClick={() => respondGroup('yes', p.userId)}
                    >
                      <span className={`respond-tickbox${(isV8 ? groupConfirmedIds.has(p.userId) && groupAnswerByCardId.get(p.userId) === true : groupAnswerClickedLive && groupAnswer === true) ? ' respond-tickbox--checked' : ''}`} />
                      {groupQuestionPeopleForCard?.length > 0 && (
                        <span className="respond-tick-avatars">
                          {groupQuestionPeopleForCard.filter((gp) => gp.photos?.[0]).slice(0, 4).map((gp, i) => (
                            <img key={gp.userId} src={gp.photos[0]} alt="" className="respond-tick-avatar" style={{ marginLeft: i > 0 ? '-8px' : 0, zIndex: 10 - i }} />
                          ))}
                        </span>
                      )}
                      {/* v8 with nobody else settled yet (e.g. asked from
                          the very first pick, before a second person has
                          been marked interested) has no names to list —
                          "In a group ()" reads as broken, so falls back to
                          a generic invite instead. v3/v7 only ever show
                          this question from the second pick onward, so
                          groupQuestionNamesForCard is never empty for
                          them — unaffected. */}
                      <span className="respond-tick-label">
                        {isV8 && !groupQuestionNamesForCard
                          ? 'In a group - with other members you want to connect with'
                          : `In a group (${groupQuestionNamesForCard})`}
                      </span>
                    </button>
                  </div>
                )}
                {isV8 && (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.6rem' }}>
                    If we can&apos;t intro you in a group, we&apos;ll still intro you individually
                  </div>
                )}
              </div>
            </InlineFade>
          </InlineFade>
          </div>
        </div>
      </div>
    );
  };

  if (showClosePanel) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner respond-fade-in" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          {closePanelPhotos.length > 0 && (
            <div className="respond-vortex">
              {closePanelPhotos.map((photo, i) => {
                const angle = (i / closePanelPhotos.length) * Math.PI * 2;
                const radius = 74;
                return (
                  <img
                    key={photo + i}
                    src={photo}
                    alt=""
                    className="respond-vortex-photo"
                    style={{
                      '--vx': `${Math.cos(angle) * radius}px`,
                      '--vy': `${Math.sin(angle) * radius}px`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                );
              })}
            </div>
          )}
          <h2 className="respond-heading">
            {hasResponded ? 'Your responses have been recorded' : "Thanks for stopping by!"}
          </h2>
          <div className="respond-actions" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
            <button type="button" className="buttonSecondary" onClick={() => setCloseClicked(false)}>← Go back</button>
          </div>
          <div className="respond-intro-sub" style={{ marginTop: '1.5rem' }}>You can close this window!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="respond-page">
      <div className="respond-page-inner" style={{ opacity: closeClicked ? 0 : 1, transition: `opacity ${CLOSE_FADE_MS}ms ease` }}>
      {simulate && (
        <div style={{ margin: '0 0 0.75rem', padding: '0.5rem 0.75rem', borderRadius: 8, background: '#fef3c7', color: '#92400e', fontSize: '0.85rem', textAlign: 'center' }}>
          Simulation of {simulate} — sample people, nothing is saved. <a href={window.location.pathname + window.location.search}>Restart</a>
        </div>
      )}
      {context.communityImage && (
        <div className="respond-hero-strip">
          <div className="respond-hero-strip-text">
            <div className="respond-hero-strip-title">{context.communityName || 'Your community'}</div>
            <div className="respond-hero-strip-brand">Introductions</div>
          </div>
          <img src={context.communityImage} alt="" className="respond-hero-strip-thumb" />
        </div>
      )}
      {isV3 ? (
        <div className="respond-letter">
          {v3Stage !== 'delay' && (
            v3Stage === 'typing1' ? (
              <TypewriterIntro paragraphs={v3IntroParagraphs1} onComplete={() => setV3Stage('leadCard')} instant={skipAnimations} />
            ) : (
              <div className="respond-typewriter">
                {v3IntroParagraphs1.map(normalizeParagraph).map((p, i) => (
                  <p key={i} className={`respond-typewriter-line${p.emphasis ? ' respond-typewriter-line--emphasis' : ''}`}>{p.text}</p>
                ))}
              </div>
            )
          )}

          {v3StageAtLeast('leadCard') && leadPeople.length > 0 && (
            <div className="respond-people respond-people--lead">
              {leadPeople.slice(0, leadRevealedCount).map((p) => (
                <React.Fragment key={p.userId}>
                  {renderPersonCard(p, {
                    className: 'respond-fade-in respond-fade-in--slow',
                    pendingSelect: true,
                    confirmed: leadConfirmedIds.has(p.userId),
                    showInlineThanks: true,
                    leadThanksVariant: true,
                    groupQuestionNames: settledInterestedNamesText,
                    groupQuestionPeople: settledInterestedPeople,
                  })}
                </React.Fragment>
              ))}
            </div>
          )}

          {v3StageAtLeast('typing2') && (
            v3Stage === 'typing2' ? (
              <TypewriterIntro paragraphs={v3IntroParagraphs2} onComplete={() => setV3Stage('restCards')} instant={skipAnimations} />
            ) : (
              <div className="respond-typewriter">
                {v3IntroParagraphs2.map(normalizeParagraph).map((p, i) => (
                  <p key={i} className={`respond-typewriter-line${p.emphasis ? ' respond-typewriter-line--emphasis' : ''}`}>{p.text}</p>
                ))}
              </div>
            )
          )}

        </div>
      ) : isV7 ? (
        <>
          <div className="respond-greeting">Hi {context.recipientFirstName || 'there'}!</div>
          <div className="respond-letter">
            <h1 className="respond-heading respond-heading--large">
              Click connect below, to be put in touch with {allSuggestedNamesText} :)
            </h1>
            <div className="respond-intro-sub">
              It looks like you have a few things in common and we think you might get on! Once you've clicked connect, we'll double check they are still keen to connect, and then intro you over messenger.
            </div>
            <div className="respond-intro-sub">
              We won't share your contact details. We will intro you over Messenger 🙂
              {' · '}
              Also, you can see your own profile <Link to={`/ProfilePreview?token=${encodeURIComponent(token)}`}>here</Link>.
            </div>
          </div>
        </>
      ) : isV8 ? (
        <>
          <div className="respond-greeting">Hi {context.recipientFirstName || 'there'}!</div>
          <div className="respond-letter">
            {isV9 ? (
              <h1 className="respond-heading">
                {context.suggestedPeople.length === 1
                  ? `Would you be interested in connecting with ${context.suggestedPeople[0].firstName || 'this person'} in ${context.communityName || 'the community'}?`
                  : `Would you be interested in connecting with any of these people in ${context.communityName || 'the community'}?`}
              </h1>
            ) : (
              <>
                <h1 className="respond-heading">
                  {keenToConnectText}
                </h1>
                <div className="respond-intro">
                  Would you be interested in connecting?
                </div>
              </>
            )}
            {/* Tight to the question above (small margin, like .respond-intro
                uses), not the usual 2rem .respond-intro-sub gap — that gap
                is reserved for the final reassurance line right before the
                cards start, just below. */}
            <div className="respond-intro-sub" style={{ marginBottom: '0.4rem' }}>
              Just click interested below.
            </div>
            <div className="respond-intro-sub">
              We think you've got a few things in common. We won't share your contact details, and you can chat over Messenger 🙂
              {' · '}
              Also, you can see your own profile <Link to={`/ProfilePreview?token=${encodeURIComponent(token)}`}>here</Link>.
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="respond-greeting">Hi {context.recipientFirstName || 'there'}!</div>
          <div className="respond-letter">
            <h1 className="respond-heading">
              {arrivedAlreadyInterested
                ? `Great — we've noted you'd like to connect with ${joinNames(arrivedInterestedNames)}!`
                : keenToConnectText}
            </h1>
            <div className="respond-intro">
              {arrivedAlreadyInterested
                ? (othersRemaining ? 'Would you also like to connect with any of these others?' : "We'll double check and be in touch shortly!")
                : 'Would you like us to put you in touch?'}
            </div>
            <div className="respond-intro-sub">
              We think you've got a few things in common. We won't share your contact details, and you can chat over Messenger 🙂
              {' · '}
              <Link to={`/ProfilePreview?token=${encodeURIComponent(token)}`}>See your own profile</Link>
            </div>
          </div>
        </>
      )}

      {actionError && <div className="respond-error">{actionError}</div>}

      {(!isV3 || v3StageAtLeast('restCards')) && restPeople.length > 0 && (
        <div className="respond-people">
          {(isV3 ? restPeople.slice(0, restRevealedCount) : restPeople).map((p) => (
            <React.Fragment key={p.userId}>
              {renderPersonCard(p, (isV3 || isV7 || isV8) ? {
                className: isV3 ? 'respond-fade-in' : undefined,
                showInlineThanks: true,
                arrivedThanksVariant: arrivedAlreadyInterested,
                groupQuestionNames: settledInterestedNamesText,
                groupQuestionPeople: settledInterestedPeople,
              } : {})}
            </React.Fragment>
          ))}
        </div>
      )}

      {isV3 && v3StageAtLeast('closing') && (
        <div className="respond-letter">
          <TypewriterIntro paragraphs={v3ClosingParagraphs} onComplete={() => setV3ClosingTypingDone(true)} instant={skipAnimations} />
        </div>
      )}

      {!isV3 && !isV7 && !isV8 && interestedCount >= 2 && (
        <div className="respond-card respond-group-card respond-fade-in">
          <div className="respond-group-question">Would you prefer to be:</div>
          <div className="respond-actions">
            <button
              type="button"
              className={groupAnswer === true ? 'button' : 'buttonSecondary'}
              disabled={busyKey === 'group:yes'}
              onClick={() => respondGroup('yes')}
            >
              {groupAnswer === true ? '✓ ' : ''}Introduced in a group chat
            </button>
            <button
              type="button"
              className={groupAnswer === false ? 'button' : 'buttonSecondary'}
              disabled={busyKey === 'group:no'}
              onClick={() => respondGroup('no')}
            >
              {groupAnswer === false ? '✓ ' : ''}Introduced 1:1 individually
            </button>
          </div>
        </div>
      )}

      {/* v3 only — the "would you like to be introduced individually or as
          a group" question no longer lives here as a single end-of-page
          prompt; it now appears inline right under whichever card was most
          recently confirmed interested, as soon as there are 2+ (see
          settledInterestedIds and renderPersonCard's showGroupQuestion
          above). This closing note just follows straight on from the
          "We won't share your contact details" paragraph. */}
      {isV3 && v3ClosingTypingDone && (
        <div className="respond-letter" style={{ marginTop: '2rem' }}>
          <TypewriterIntro
            paragraphs={[
              {
                text: 'P.S., If you want to check your profile, you can click here.',
                link: { text: 'click here', to: `/ProfilePreview?token=${encodeURIComponent(token)}` },
              },
            ]}
            onComplete={() => setV3FinalNoteTypingDone(true)}
            instant={skipAnimations}
          />
        </div>
      )}

      {(isV3 || isV7 || isV8) ? (
        // No aggregate summary for v3/v7/v8 — each card that gets a fresh
        // Connect click already shows its own inline "we'll double check
        // with X" note right there (see renderPersonCard's
        // showInlineThanks), which would otherwise duplicate this exact
        // message.
        null
      ) : (
        interestedCount >= 1 && (
          <div className="respond-letter" style={{ marginTop: '2rem' }}>
            <div className="respond-intro">Thanks! We'll double check with {interestedNamesText}, and will be in touch shortly!</div>
          </div>
        )
      )}

      {/* v7/v8 only — the page just ends here, no P.S./profile-link note
          (see their own headers, which already link to the profile) and
          deliberately no Close window section at all (see the exclusion
          below) — the recipient just closes the tab/email themselves. */}
      {(isV7 || isV8) && (
        <div className="respond-letter" style={{ marginTop: '2rem' }}>
          <div className="respond-intro">Thanks!</div>
          <div className="respond-intro-sub" style={{ marginTop: '1.25rem' }}>
            {context.communityName || 'Your community'} - Interconnector
          </div>
        </div>
      )}

      {(!isV3 || v3FinalNoteTypingDone) && !isV7 && !isV8 && (
      <div className={`respond-done${isV3 ? ' respond-fade-in respond-fade-in--slow' : ''}`}>
        {/* Clicking this fades the whole page out and swaps to the
            dedicated closing panel above (see showClosePanel) — no need
            for this button's own inline "done" state anymore. */}
        <button
          type="button"
          className="button-fancy"
          onClick={() => setCloseClicked(true)}
        >
          Close window
        </button>
        <div className="respond-done-note">Your choices above are already saved.</div>
      </div>
      )}
      <div ref={contentEndRef} />
      </div>

      {enlargedPhoto && (
        <div className="respond-photo-overlay" onClick={() => setEnlargedPhoto(null)}>
          <img src={enlargedPhoto} alt="" className="respond-photo-overlay-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default RespondToSuggestion;
